/**
 * MetaConnectedModal — full-screen celebration shown after a successful
 * Meta OAuth connection (WhatsApp, Facebook, Instagram).
 */
import React, { useState, useEffect } from 'react';
import './MetaConnectedModal.css';

const PLATFORM_CONFIG = {
  whatsapp: { icon: '💬', label: 'WhatsApp', color: '#22c55e', desc: 'Business API connected. AI now handles your WhatsApp conversations 24/7.' },
  facebook: { icon: '📘', label: 'Facebook', color: '#1877f2', desc: 'Facebook Messenger connected. Customer messages will flow into your unified inbox.' },
  instagram: { icon: '📷', label: 'Instagram', color: '#e1306c', desc: 'Instagram DMs connected. Respond to followers automatically.' },
};

const STEPS = [
  { icon: '⚙️', title: 'Your channels are connected', desc: 'Meta has verified your credentials and Oakdesk has stored them securely.' },
  { icon: '🤖', title: 'AI is ready to respond', desc: 'Your AI receptionist will now answer messages on connected platforms based on your business profile.' },
  { icon: '📥', title: 'Check your unified inbox', desc: 'All incoming messages from connected channels will appear in one place.' },
];

export default function MetaConnectedModal({ platforms = [], onDismiss, onNavigate }) {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setStep(s => (s + 1) % STEPS.length);
    }, 2800);
    return () => clearInterval(t);
  }, []);

  const connectedPlatforms = platforms.filter(p => PLATFORM_CONFIG[p]);

  return (
    <div className="mcm-overlay">
      <div className="mcm-card">
        {/* Confetti dots */}
        <div className="mcm-confetti" aria-hidden="true">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="mcm-confetti__dot" style={{ '--i': i }} />
          ))}
        </div>

        {/* Success icon */}
        <div className="mcm-success-ring">
          <div className="mcm-success-icon">✓</div>
        </div>

        {/* Headline */}
        <h1 className="mcm-title">You're connected!</h1>
        <p className="mcm-sub">
          {connectedPlatforms.length > 0
            ? `${connectedPlatforms.map(p => PLATFORM_CONFIG[p]?.label).join(' and ')} ${connectedPlatforms.length === 1 ? 'is' : 'are'} now live.`
            : 'Your Meta accounts are now connected to Oakdesk.'}
        </p>

        {/* Connected platform badges */}
        {connectedPlatforms.length > 0 && (
          <div className="mcm-platforms">
            {connectedPlatforms.map(p => {
              const cfg = PLATFORM_CONFIG[p];
              return (
                <div key={p} className="mcm-platform" style={{ borderColor: cfg.color + '44', background: cfg.color + '12' }}>
                  <span className="mcm-platform__icon">{cfg.icon}</span>
                  <div>
                    <div className="mcm-platform__name" style={{ color: cfg.color }}>{cfg.label}</div>
                    <div className="mcm-platform__desc">{cfg.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rotating what-happens-next steps */}
        <div className="mcm-steps">
          {STEPS.map((s, i) => (
            <div key={i} className={`mcm-step${i === step ? ' mcm-step--active' : ''}`}>
              <span className="mcm-step__icon">{s.icon}</span>
              <div>
                <div className="mcm-step__title">{s.title}</div>
                <div className="mcm-step__desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Step dots */}
        <div className="mcm-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`mcm-dot${i === step ? ' mcm-dot--active' : ''}`} onClick={() => setStep(i)} />
          ))}
        </div>

        {/* Actions */}
        <div className="mcm-actions">
          <button className="mcm-btn mcm-btn--primary" onClick={() => onNavigate('unified-inbox')}>
            Open inbox →
          </button>
          <button className="mcm-btn mcm-btn--secondary" onClick={() => onNavigate('social-channels')}>
            Configure channels
          </button>
          <button className="mcm-btn mcm-btn--ghost" onClick={onDismiss}>
            Dismiss
          </button>
        </div>

        <p className="mcm-note">
          Your AI receptionist is now handling messages on connected platforms based on your business profile.
          <button className="mcm-link" onClick={() => onNavigate('business-context')}> Update AI settings →</button>
        </p>
      </div>
    </div>
  );
}
